/**
 * Order Confirmation Template Service
 * Generates WhatsApp and Email templates for order confirmation after payment
 */

/**
 * Generate WhatsApp template for order confirmation
 * @param {Object} quote - Quote object
 * @param {Object} order - Order object
 * @param {string} paymentReference - Payment reference
 * @param {string} confirmationType - Payment confirmation type (proof_received | money_cleared)
 * @returns {string} Formatted WhatsApp message
 */
export const generateOrderConfirmationWhatsApp = (
  quote,
  order,
  paymentReference,
  confirmationType
) => {
  const customerName = quote.customerName || "there";
  const orderId = order?.orderId || order?.id || "N/A";
  const quoteId = quote.id || quote.referenceNumber || "N/A";
  const totalAmount = quote.estimatedCost
    ? `R${quote.estimatedCost.toLocaleString("en-ZA", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : "N/A";

  const confirmationNote =
    confirmationType === "money_cleared"
      ? "Payment has been cleared in our account"
      : "Payment proof has been received";

  return `🎉 *Payment Confirmed & Order Created!*

Thank you for your payment! Your order has been successfully created.

*Order Details:*
🆔 Order ID: *${orderId}*
💰 Total Amount: ${totalAmount}
💳 Payment Reference: ${paymentReference}
📋 Quote Reference: ${quoteId}
✅ ${confirmationNote}

*Next Steps:*
Our team will contact you within the next 2 hours to:
• Confirm pickup details
• Arrange pickup time
• Discuss any special requirements

📞 For immediate assistance, please contact us.

Thank you for choosing Bubakii! 🚚`;
};

/**
 * Generate Email template for order confirmation
 * @param {Object} quote - Quote object
 * @param {Object} order - Order object
 * @param {string} paymentReference - Payment reference
 * @param {string} confirmationType - Payment confirmation type (proof_received | money_cleared)
 * @returns {Object} Email template with subject and body
 */
export const generateOrderConfirmationEmail = (
  quote,
  order,
  paymentReference,
  confirmationType
) => {
  const customerName = quote.customerName || "Valued Customer";
  const orderId = order?.orderId || order?.id || "N/A";
  const quoteId = quote.id || quote.referenceNumber || "N/A";
  const totalAmount = quote.estimatedCost
    ? `R${quote.estimatedCost.toLocaleString("en-ZA", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : "N/A";

  const confirmationNote =
    confirmationType === "money_cleared"
      ? "Payment has been cleared in our account"
      : "Payment proof has been received";

  const subject = `Payment Confirmed - Order ${orderId}`;

  const body = `Dear ${customerName},

Thank you for your payment! Your order has been successfully created.

ORDER DETAILS:
• Order ID: ${orderId}
• Total Amount: ${totalAmount}
• Payment Reference: ${paymentReference}
• Quote Reference: ${quoteId}
• Payment Status: ${confirmationNote}

NEXT STEPS:
Our team will contact you within the next 2 hours to:
• Confirm pickup details
• Arrange pickup time
• Discuss any special requirements

If you have any questions or need immediate assistance, please contact us.

Thank you for choosing Bubakii Logistics!

Best regards,
Bubakii Logistics Team`;

  return {
    subject,
    body,
  };
};
